import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation, useParams } from 'react-router-dom';
import { collection, doc, getDocs, getDoc, serverTimestamp, setDoc, deleteDoc } from 'firebase/firestore';
import { db, auth, ensureAuth } from "@/lib/firebase";
import { getQuizRef } from '@/data/quizzes';
import { log } from '@/utils/logger';
import { handleError } from '../utils/errorHandler';
import { useToast } from '../components/Toaster';
import { ensureUniqueName } from '@/utils/participants';
import { useAuth } from '../context/AuthContext';

export default function JoinQuiz() {
  const { code, id } = useParams<{ code?: string; id?: string }>();
  const [quizId, setQuizId] = useState('');
  const [name, setName] = useState('');
  const location = useLocation();
  const [errors, setErrors] = useState<{ name?: string; quizId?: string }>(() => {
    const err = (location.state as any)?.error as string | undefined;
    return err ? { quizId: err } : {};
  });
  const navigate = useNavigate();
  const [copied, setCopied] = useState(false);
  const { show } = useToast();
  const { user } = useAuth();

  useEffect(() => {
    // Use code from URL param if available, otherwise use id
    const urlQuizId = code || id;
    if (urlQuizId) {
      setQuizId(urlQuizId);
    }
  }, [code, id]);

  const randomName = () => {
    const adjectives = ['Witty', 'Brave', 'Curious', 'Clever', 'Speedy', 'Lucky'];
    const nouns = ['Walrus', 'Penguin', 'Otter', 'QuizWhiz', 'Ninja', 'Genius'];
    const adj = adjectives[Math.floor(Math.random() * adjectives.length)];
    const noun = nouns[Math.floor(Math.random() * nouns.length)];
    const num = Math.floor(Math.random() * 100);
    return `${adj}${noun}${num}`;
  };

  const surprise = () => {
    setName(randomName());
  };

  // Removed automatic anonymous sign-in - users should explicitly sign in
  // useEffect(() => {
  //   ensureAuth().catch(() => {
  //     /* noop */
  //   });
  // }, []);

  const join = async () => {
    const errs: any = {};
    if (!name.trim()) errs.name = 'Please enter your name.';
    if (!quizId.trim()) errs.quizId = 'Please enter a quiz ID.';
    if (Object.keys(errs).length) {
      setErrors(errs);
      const first = Object.keys(errs)[0];
      document.getElementById(first === 'name' ? 'joinName' : 'joinQuizId')?.focus();
      return;
    }
    setErrors({});

    const qId = quizId.trim();
    const baseName = name.trim();
    
    try {
      // Ensure user is authenticated (sign in anonymously if needed)
      let currentUser = auth.currentUser;
      if (!currentUser || !currentUser.uid) {
        try {
          await ensureAuth();
          currentUser = auth.currentUser;
        } catch (err) {
          console.warn('Failed to authenticate anonymously:', err);
        }
      }
      
      // If still no user, show error
      if (!currentUser || !currentUser.uid) {
        setErrors({ quizId: 'Please sign in to join a quiz.' });
        show({ title: 'Authentication Required', description: 'You must sign in to join a quiz', type: 'error' });
        navigate('/login');
        return;
      }
      const userId = currentUser.uid;

      const participantsCol = collection(getQuizRef(db, qId), 'participants');
      
      // Check if quiz exists first
      const quizSnap = await getDoc(getQuizRef(db, qId));
      if (!quizSnap.exists()) {
        setErrors({ quizId: 'Quiz not found' });
        await log.error('join_nonexistent_quiz', { 
          message: `User attempted to join non-existent quiz: ${qId}`,
          quizId: qId 
        });
        show({ title: 'Not found', description: 'Quiz not found', type: 'error' });
        return;
      }
      
      // Get existing participants to ensure unique name
      const snap = await getDocs(participantsCol);
      const existing = snap.docs.map(d => {
        const data = d.data() as any;
        return data.nickname || data.displayName || data.name || '';
      });
      const finalName = ensureUniqueName(existing, baseName);
      
      // Add participant to the quiz
      await setDoc(doc(participantsCol, userId), {
        nickname: finalName,
        displayName: finalName,
        joinedAt: serverTimestamp(),
        userAgent: navigator.userAgent,
        answerStatus: 'active',
      });
      
      log.info('participant_joined', { 
        message: `Participant "${finalName}" joined quiz`,
        quizId: qId, 
        nickname: finalName 
      });
      localStorage.setItem('participantName', finalName);
      setErrors({});
      
      // Navigate to the waiting room
      navigate(`/room/${qId}`);
    } catch (err: any) {
      console.error('Join quiz error:', err);
      let errorMessage = 'Failed to join quiz.';
      
      // Provide specific error messages for common issues
      if (err.code === 'auth/admin-restricted-operation') {
        errorMessage = 'Anonymous authentication is not enabled. Please enable it in Firebase Console.';
        show({ 
          title: 'Configuration Error', 
          description: 'Anonymous sign-in must be enabled in Firebase Authentication settings.', 
          type: 'error' 
        });
      } else if (err.code === 'permission-denied') {
        errorMessage = 'Permission denied. Please check Firestore security rules.';
      } else {
        errorMessage = err.message || errorMessage;
      }
      
      setErrors({ quizId: errorMessage });
      await handleError(err, { action: 'joinQuiz', input: { quizId: qId, name: baseName } }, qId, show);
    }
  };

  return (
    <div className="max-w-md mx-auto mt-8">
      <div className="shadow-lg border rounded-lg p-6 bg-white space-y-4">
        <h2 className="text-2xl font-semibold text-center">Join a Quiz</h2>
        
        <div className="space-y-1">
          <label htmlFor="joinName" className="block text-sm font-medium text-gray-700">
            Your Name
          </label>
          <input
            id="joinName"
            className="w-full border border-gray-300 rounded-md p-3 focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            placeholder="Enter your name"
            value={name}
            aria-invalid={Boolean(errors.name)}
            aria-describedby={errors.name ? 'joinName-error' : undefined}
            onChange={e => setName(e.target.value)}
            onKeyPress={e => e.key === 'Enter' && join()}
          />
          {errors.name && <p id="joinName-error" className="text-red-500 text-sm mt-1">{errors.name}</p>}
        </div>
        
        <button
          type="button"
          className="w-full py-2 px-4 rounded-md bg-gray-100 text-gray-700 hover:bg-gray-200 transition text-sm font-medium"
          onClick={surprise}
        >
          🎲 Surprise Me with a Random Name
        </button>
        
        <div className="space-y-1">
          <label htmlFor="joinQuizId" className="block text-sm font-medium text-gray-700">
            Quiz ID
          </label>
          <input
            id="joinQuizId"
            className="w-full border border-gray-300 rounded-md p-3 focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            placeholder="Enter quiz ID"
            value={quizId}
            aria-invalid={Boolean(errors.quizId)}
            aria-describedby={errors.quizId ? 'joinQuizId-error' : undefined}
            onChange={e => setQuizId(e.target.value)}
            readOnly={Boolean(code || id)}
            onKeyPress={e => e.key === 'Enter' && join()}
          />
          {errors.quizId && <p id="joinQuizId-error" className="text-red-500 text-sm mt-1">{errors.quizId}</p>}
        </div>
        
        {(code || id) && (
          <button
            type="button"
            className="w-full py-2 px-4 text-sm bg-gray-100 rounded-md text-gray-700 hover:bg-gray-200 transition"
            onClick={() => {
              navigator.clipboard.writeText(`${window.location.origin}/join/${code || id}`);
              setCopied(true);
              setTimeout(() => setCopied(false), 1500);
            }}
          >
            {copied ? '✓ Link Copied!' : '📋 Copy Join Link'}
          </button>
        )}
        
        <button
          type="button"
          className="w-full py-3 px-4 rounded-md bg-[#591CFF] text-white font-semibold hover:opacity-90 focus:outline-none focus:ring-2 focus:ring-[#591CFF] focus:ring-offset-2 transition shadow-md"
          onClick={join}
        >
          Join Quiz →
        </button>
      </div>
    </div>
  );
}

